import React, { useContext, useCallback, useMemo } from 'react';
import _ from 'lodash';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { withRouter } from 'react-router-dom';
import { PremierConstants } from 'premier-constant-service';
import { useUpdateSubmissionServices, validationUtil } from 'premier-capability-hooks';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import metadata from './RiskDetailsPage.metadata.json5';
import styles from './RiskDetailsPage.module.scss';
import UnderwritingComponent from '../../components/Underwriting/UnderwritingComponent.jsx';
import { BreakpointTrackerContext } from '@jutro/legacy/layout';

function RiskDetailsPage(props) {
    const { wizardData: submissionVM, updateWizardData, history } = props;
    const breakpoint = useContext(BreakpointTrackerContext);
    const { initialValidation, isComponentValid, onValidate } = useValidation('RiskDetailsPage');
    const isNewSubmission = useMemo(() => history.location.state ? history.location.state.quoteentry.isNewSubmission : false, [history]);
    const { updateSubmission } = useUpdateSubmissionServices();
    const { lineName } = PremierConstants;

    const getPathUpdated = useCallback(() => {
        let riskDetailPath = ''

        switch (_.get(submissionVM.value, 'lobData.perLine.selectedOffering_Ext')) {
            case 'Cond':
                riskDetailPath = 'lobData.perLine.coverables.condoCoverable.condoRiskDetails'
                break;

            case 'RisAssDwe':
                riskDetailPath = 'lobData.perLine.coverables.riskAssistDwellingsCoverable.risAssDweRiskDetails'
                break;

            case 'PerProInSto':
                riskDetailPath = 'lobData.perLine.coverables.propertyStorageCoverable.propInStorageRiskDetails'
                break;

            case 'MobHom':
                riskDetailPath = 'lobData.perLine.coverables.mobileHomeCoverable.mobHomRiskDetails'
                break;

            case 'TinHom':
                riskDetailPath = 'lobData.perLine.coverables.tinyHomesCoverable.tinyHomesRiskDetails'
                break;
            case 'PerUmbLia':
                riskDetailPath = 'lobData.perLine.coverables.perUmbLiaCoverable.personalUmbRiskDetails'
                break;
            default:
                riskDetailPath = ''
        }

        return riskDetailPath

    }, [submissionVM.value])

    const pathUpdated = getPathUpdated()
    const modelVM = _.get(submissionVM, pathUpdated)

    modelVM.value.isValidationReq = true;

    const onNext = useCallback(async () => {
        const productCode = _.get(submissionVM.value, 'baseData.productCode');
                // eslint-disable-next-line security/detect-object-injection
        const lobLineName = productCode ? lineName[productCode] : '';
        const response = await updateSubmission(lobLineName, 'RiskDetailsPage', submissionVM);
                    
        submissionVM.value = response;

        return submissionVM;
    }, [lineName, submissionVM, updateSubmission]);

    const onSave = useCallback(
        async () => {
            const subVMCopy = _.clone(submissionVM)
            const productCode = _.get(subVMCopy.value, 'baseData.productCode');
        
            validationUtil.unSetIsValidationReqFlag(_.get(subVMCopy, `${pathUpdated}.value`), ' ')

            const lobLineName = productCode ? lineName[productCode] : '';
            await updateSubmission(lobLineName, 'RiskDetailsPage', subVMCopy);
                            
            return submissionVM;
        },
        [lineName, submissionVM, updateSubmission]
    );
    
    const getOverrideProps = () => ({
        '@field': {
            showRequired: true,
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top'
        },
        underwriting: {
            overrideProps: {
                "@field": {
                    // apply to all fields
                    showRequired: true,
                    labelPosition: "left",
                },
            },
            submissionVM,
            updateWizardData,
            visible: _.get(submissionVM.value, 'lobData.perLine.selectedOffering_Ext') === 'PerUmbLia',
            // eslint-disable-next-line no-secrets/no-secrets
            pathUpdated: "lobData.perLine.coverables.perUmbLiaCoverable.personalUmbUnderwriting"
        },
        RisAssDweRiskDetailsContainer: {
            visible: _.get(submissionVM.value, 'lobData.perLine.selectedOffering_Ext') === 'RisAssDwe'
        },
        CondoRiskDetailsContainer: {
            visible: _.get(submissionVM.value, 'lobData.perLine.selectedOffering_Ext') === 'Cond'
        },
        MobHomRiskDetailsContainer: {
            visible: _.get(submissionVM.value, 'lobData.perLine.selectedOffering_Ext') === 'MobHom'
        },
        TinyHomesRiskDetailsContainer: {
            visible: _.get(submissionVM.value, 'lobData.perLine.selectedOffering_Ext') === 'TinHom'
        },
        PerUmbLiaRiskDetailsContainer: {
            visible: _.get(submissionVM.value, 'lobData.perLine.selectedOffering_Ext') === 'PerUmbLia'
        },
        propertyInStorageDetailsContainer: {
            visible: _.get(submissionVM.value, 'lobData.perLine.selectedOffering_Ext') === 'PerProInSto'
        },
        ifyesexpFie1: {
            visible: _.get(submissionVM.value, 'lobData.perLine.selectedOffering_Ext') === "RisAssDwe"
                && !_.isUndefined(_.get(modelVM, 'hasanyinsevecandecforFie.value'))
                && _.get(modelVM, 'hasanyinsevecandecforFie.value'),
            required: _.get(submissionVM.value, 'lobData.perLine.selectedOffering_Ext') === "RisAssDwe"
                && !_.isUndefined(_.get(modelVM, 'hasanyinsevecandecforFie.value'))
                && _.get(modelVM, 'hasanyinsevecandecforFie.value')
        },
        ifyesexpFie2: {
            visible: _.get(modelVM, 'hasthirisbeedecrefinsFie.value'),
            required: _.get(modelVM, 'hasthirisbeedecrefinsFie.value')
        },
        ifyespleprodetFie: {
            visible: _.get(modelVM, 'havyouevehadinscanmidFie.value'),
            required: _.get(modelVM, 'havyouevehadinscanmidFie.value') 
        },
        ifyespleprodet2Fie: {
            visible: _.get(modelVM, 'hasyouinsbeecandueoccFie.value'),
            required: _.get(modelVM, 'hasyouinsbeecandueoccFie.value')  
        },
        hasanyinsevecandecforFie1: {
            required: true,
            defaultValue: false
        },
        ifyesexpFie3: {
            visible: _.get(submissionVM.value, 'lobData.perLine.selectedOffering_Ext') === "MobHom"
                && !_.isUndefined(_.get(modelVM, 'hasanyinsevecandecforFie.value'))
                && _.get(modelVM, 'hasanyinsevecandecforFie.value'),
            required: _.get(submissionVM.value, 'lobData.perLine.selectedOffering_Ext') === "MobHom"
                && !_.isUndefined(_.get(modelVM, 'hasanyinsevecandecforFie.value'))
                && _.get(modelVM, 'hasanyinsevecandecforFie.value')
        },
        ifyes2exFie: {
            visible: _.get(modelVM, 'havyouevehadinsthanonFie.value'),
            required: _.get(modelVM, 'havyouevehadinsthanonFie.value')
        },
        reastamarchonottorrenFie: {
            visible: _.get(modelVM, 'hasyouevehadinsrefmarFie.value'),
            required: _.get(modelVM, 'hasyouevehadinsrefmarFie.value')
        },
        doyouoranylivundyoothFie: {
            visible: _.get(submissionVM, 'baseData.baseState_Ext.value.code') === 'QC'
        },
        ifYesExp4Tiny: {
            visible: _.get(submissionVM.value, 'lobData.perLine.selectedOffering_Ext') === "TinHom"
                && !_.isUndefined(_.get(modelVM, 'hasanyinsevecandecforFie.value'))
                && _.get(modelVM, 'hasanyinsevecandecforFie.value'),
            required: _.get(submissionVM.value, 'lobData.perLine.selectedOffering_Ext') === "TinHom"
                && !_.isUndefined(_.get(modelVM, 'hasanyinsevecandecforFie.value'))
                && _.get(modelVM, 'hasanyinsevecandecforFie.value')
        },
        hasTheBeeAnyGapInCovFieTiny: {
            defaultValue: false
        },
        anyLosOrIncDurTheGapFieTiny: {
            visible: _.get(modelVM, 'hasTheBeeAnyGapInCovFie.value')
        },
        numOfDayOfGapTiny: {
            visible: !_.isUndefined(_.get(modelVM, 'anyLosOrIncDurTheGapFie.value')) && _.get(modelVM, 'anyLosOrIncDurTheGapFie.value', false) && _.get(modelVM, 'hasTheBeeAnyGapInCovFie.value', false)
        }
    });


    const resolvers = {
        resolveComponentMap: {
            underwriting: UnderwritingComponent
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {}
    }

    const isPageInitialValidation = async () => { 
        const isIntialValidation = await initialValidation()
 
        return isIntialValidation && !isNewSubmission 
    };

    const readValue = useCallback(
        (id, path) => readViewModelValue(metadata.pageContent,
            _.get(submissionVM, pathUpdated), id, path, getOverrideProps()),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [pathUpdated, submissionVM]
        );

    const handleValueChange = useCallback((value, changedPath) => {
        const fullPath = `${pathUpdated}.${changedPath}`;
        
        _.set(submissionVM, `${fullPath}`, value);
        
        updateWizardData(submissionVM);
    }, [pathUpdated, submissionVM, updateWizardData]);

    const isDisableNext = () => !submissionVM.lobData.perLine.coverables.perUmbLiaCoverable.aspects.subtreeValid
    
    return (
        <WizardPage
            showPrevious
            skipWhen={isPageInitialValidation}
            disableNext={isDisableNext()}
            onNext={onNext}
            onSave={onSave}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                resolveValue={readValue}
                overrideProps={getOverrideProps()}
                onValueChange={handleValueChange}
                onValidationChange={onValidate}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
            />
        </WizardPage>
    );
}

RiskDetailsPage.propTypes = wizardProps;
export default withRouter(RiskDetailsPage);